import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Shield, Target, Users, Award, Zap, CheckCircle } from 'lucide-react';

export const AboutPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-white text-zinc-900 overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32 mt-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-6xl md:text-7xl font-bold text-blue-600 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sightline UAV
            </motion.h1>
            <motion.p
              className="text-2xl text-zinc-700 max-w-3xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Precision-driven aerial operations led by former IDF specialists in Herzelia, Israel.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <p className="text-lg text-zinc-700 leading-relaxed">
              Founded in the heart of Israel's innovation ecosystem, SightLine UAV embodies the nation's enduring commitment to protection, precision, and progress. Drawing inspiration from the timeless belief in a watchful presence above, we deploy state-of-the-art drone systems that serve as vigilant guardians for communities, industries, and frontiers. From desert outposts, through dense forests to urban skylines, our technology delivers clarity, safety, and peace of mind—24/7.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">Border Security</h3>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  Our advanced thermal and innovative software, allow our drone fleets to provide persistent aerial surveillance over high-risk perimeters. Capable of detecting human or vehicle movement up to 3 km away, even in total darkness, fog, or sandstorms, our systems deliver real-time intelligence streams. Designed for seamless integration with existing military and national security command infrastructures, we enhance situational awareness without replacing trusted protocols—strengthening the shield that protects sovereign borders.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">Search & Rescue</h3>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  In critical moments, every second matters. Our rapid-deployment drones equipped with thermal imaging, loudspeakers, and GPS precision locate lost individuals in rugged terrains like the Judean Desert or disaster-stricken areas. By relaying live coordinates and visuals to first responders, we bridge the gap between crisis and rescue—saving lives when hope hangs in the balance.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">Real Estate Services</h3>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  Transform property marketing with breathtaking aerial cinematography, interactive 3D models, and immersive virtual tours. Whether showcasing luxury high-rises in Tel Aviv, sprawling agricultural estates in the Negev, or coastal villas in Herzliya, our professional-grade imagery accelerates sales and captivates buyers from the first frame.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">Infrastructure Monitoring</h3>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  Prevent costly disruptions with proactive aerial inspections. From power transmission lines and wind turbines to bridges and solar farms, our drones capture high-resolution data, detect structural anomalies, and generate detailed reports—all with centimeter-level accuracy. Utilities and public works agencies rely on us to maintain operational continuity and public safety.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-lg border border-blue-200">
              <h3 className="text-2xl font-bold text-zinc-900 mb-4">Leadership Forged in Fire</h3>
              <p className="text-lg text-zinc-700 leading-relaxed mb-4">
                Every mission at SightLine UAV is guided by a leadership team of IDF specialists who served on Israel's volatile northern frontier in the last two years. These veterans operated UAVs under live fire, managed cross-border incursions, and coordinated real-time intel with ground forces in Lebanon-adjacent sectors. Their expertise translates into:
              </p>
              <ul className="space-y-3 text-zinc-700">
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-blue-600 mt-1">•</span>
                  <span><strong>Split-second threat triage</strong> – distinguishing civilians from hostiles in cluttered environments</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-blue-600 mt-1">•</span>
                  <span><strong>Zero-downtime operations</strong> – flying in electronic warfare zones with jammed GPS and comms</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-blue-600 mt-1">•</span>
                  <span><strong>Ironclad protocols</strong> – from pre-flight risk assessment to post-mission debriefs that exceed NATO standards</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-blue-600 mt-1">•</span>
                  <span><strong>Field-proven integration</strong> – linking drone feeds to ground systems without latency or data loss</span>
                </li>
              </ul>
              <p className="text-lg text-zinc-700 leading-relaxed mt-4 italic">
                This isn't classroom theory; it's muscle memory forged on the same ridges and valleys our systems now protect.
              </p>
            </div>
          </motion.div>
        </div>
      </section>





      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose Sightline UAV?</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Military-Grade Precision: Decades of operational expertise in high-stakes environments',
              'Uncompromising Safety: Proven protocols that exceed all regulatory requirements',
              '24/7 Operations Capability: Ready for emergency response and critical missions',
              'Advanced Technology: State-of-the-art equipment and cutting-edge sensor systems',
              'Rapid Deployment: Years of military experience in mission readiness',
              'Comprehensive Solutions: From initial assessment to complete project delivery',
              'Local Expertise: Deep understanding of Israeli geography and regional dynamics',
              'Client Success Focus: Your objectives become our operational priorities',
            ].map((point, index) => (
              <motion.div
                key={index}
                className="flex items-start gap-4 p-6 bg-white/10 rounded-lg hover:bg-white/20 transition-all duration-300"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                <span className="text-lg text-white">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-zinc-900 mb-6">Headquartered in Herzelia, Israel</h2>
            <p className="text-lg text-zinc-700 mb-8">
              Positioned at the heart of Israel's technology hub, Sightline UAV operates with local expertise and global reach. Our Herzelia base provides strategic advantages for rapid response and comprehensive regional coverage.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Visit Us or Schedule a Meeting
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
